import {gql} from '@apollo/client';
import {objectFromEntries} from '@benchling/js-utils';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {MinimalExpirationInfo} from 'constants/SampleStatusConstants';
import {StorableWithValidContentsInfo} from 'constants/SampleStatusConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {getLatestVersionId} from 'notebook/structured-tables/ActionFunctionDAO';
import {Cell} from 'notebook/tables/TableConstants';
import {getStorableIdsFromTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {
  ContainersSearchListingValidContentsQuery,
  ContainersSearchListingValidContentsQueryVariables,
} from 'samples/containers/__generated/ContainersSearchListingValidContentsQuery';
import APIUtil from 'util/APIUtil';

export const CONTAINERS_SEARCH_LISTING_VALID_CONTENTS_QUERY = gql`
  query ContainersSearchListingValidContentsQuery($containerIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        expirationInfo {
          id
          expirationDate
          isExpired
          isTrackingEnabled
        }
        freezeThawInfo {
          id
          freezeThawCount
          isTrackingEnabled
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ContainerValidContentsInfoById {
  [containerId: string]: StorableWithValidContentsInfo;
}

export interface ContainerValidContentsQueryResponse {
  type: QueryResultType;
  validContentsInfoByContainerId: ContainerValidContentsInfoById;
  refetch: () => void;
}

async function doContainerValidContentsQuery(
  containerIds: Array<string>
): Promise<Array<[string, MinimalExpirationInfo]>> {
  containerIds = [...new Set(containerIds)].filter((id) => APIUtil.isContainer(id));
  if (
    containerIds.length === 0 ||
    !(BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE || BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW)
  ) {
    return [];
  }
  const data = await doQuerySafe<
    ContainersSearchListingValidContentsQuery,
    ContainersSearchListingValidContentsQueryVariables
  >({query: CONTAINERS_SEARCH_LISTING_VALID_CONTENTS_QUERY}, {variables: {containerIds}});
  return accessibleObjs(data.containers).map((item) => [item.id, item.expirationInfo]);
}

/**
 * For each [event, cells] tuple, tracks the validity of the containers
 * in the given cells for the given event.
 */
export async function trackContainerValidityForTableEvents(
  fileId: string,
  tableApiId: string,
  analyticsEventMappings: Array<{
    event: analytics.AnalyticsEvent;
    cells: Array<Cell>;
  }>
): Promise<void> {
  if (!BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE) {
    return;
  }
  const eventToContainerIds = new Map();
  // Convert cells to a list of ids of their container contents
  // Collect all containers in a single list for one query across all events
  for (const mapping of analyticsEventMappings) {
    const eventContainerIds = mapping.cells.flatMap((cell) =>
      getStorableIdsFromTypedValue(cell.typedValue ?? null)
    );
    if (eventContainerIds.length > 0) {
      eventToContainerIds.set(mapping.event, eventContainerIds);
    }
  }

  const expirationInfoById = await doContainerValidContentsQuery(
    Array.from(eventToContainerIds.values()).flat()
  );
  if (expirationInfoById.length === 0) {
    return;
  }
  eventToContainerIds.forEach((eventContainerIds, analyticsEvent) => {
    const expirationInfos = expirationInfoById
      .filter(([id, expirationInfo]) => eventContainerIds.includes(id))
      .map(([id, expirationInfo]) => expirationInfo);
    let validContainers: number = 0;
    let expiredContainers: number = 0;
    for (const expirationInfo of expirationInfos) {
      if (expirationInfo.isTrackingEnabled) {
        if (expirationInfo.isExpired) {
          expiredContainers++;
        } else {
          validContainers++;
        }
      }
    }
    analytics.trackEvent(analyticsEvent, {
      fileId,
      structured_table_api_identifier: tableApiId,
      latest_file_version_id: getLatestVersionId(fileId),
      total_container_count: expirationInfos.length,
      valid_container_count: validContainers,
      expired_container_count: expiredContainers,
    });
  });
}

// Undefined is returned when the query is skipped - meaning valid contents is not enabled or none of the items
// are containers. This is done intentionally to prevent extra rerenders on the ExpandedItemsTable if there are
// no meaningful state changes
// See https://github.com/benchling/aurelia/pull/2728 for more details
export function useContainerValidContentsQuery(
  items: Array<{id: string}>
): ContainerValidContentsQueryResponse | undefined {
  const containerIds = items.flatMap((item) => (APIUtil.isContainer(item.id) ? [item.id] : []));
  const result = useSkippableBenchlingQuery<
    ContainersSearchListingValidContentsQuery,
    ContainersSearchListingValidContentsQueryVariables
  >(CONTAINERS_SEARCH_LISTING_VALID_CONTENTS_QUERY, {
    skip:
      containerIds.length === 0 ||
      !(BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE || BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW),
    variables: {containerIds},
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: true,
  });

  const baseResponse = {type: result.type, refetch: result.refetch};
  if (result.type === QueryResultType.SKIPPED) {
    return undefined;
  } else if (result.type === QueryResultType.PENDING || result.type === QueryResultType.ERROR) {
    return {...baseResponse, validContentsInfoByContainerId: {}};
  } else {
    return {
      ...baseResponse,
      validContentsInfoByContainerId: objectFromEntries(
        accessibleObjs(result.data.containers).map((item) => [item.id, item])
      ),
    };
  }
}
